import {
  CIMSPage,
  FieldDesignChangeStatus,
  alphabeticalCompare,
  alphabeticalSort,
} from 'shared'
import { FieldDesignChange } from '../../../../FDCTrackerContext/types'
import { HelpMessage } from 'utils/constants'
import { CIMSTag } from 'components/CIMSTag'
import { CIMSTags } from 'components/CIMSTags'
import ConcurrenceGroups from './components/ConcurrenceGroups'
import ApprovalGroups from './components/ApprovalGroups'
import { useFDCTrackerContext } from '../../../../FDCTrackerContext'
import Resolution from './components/Resolution'
import Status from './components/Status'
import { CIMSColumnsOptions } from 'components/CIMSTable/CIMSColumn/types'

export function useColumns(): CIMSColumnsOptions<FieldDesignChange> {
  const {
    metadata,
    fieldDesignChanges,
    mutations: { edit },
    externalLinks,
  } = useFDCTrackerContext()

  return {
    columns: [
      {
        title: 'Status',
        help: HelpMessage.FieldDesignChanges.FDCTracker.Status,
        render: (record) => <Status {...record} />,
        align: 'center',
        width: 125,
        sort: {
          with: 'callback',
          onCompare: (a, b) => {
            const A = FieldDesignChangeStatus.fromIdOrFail(a.statusId)
            const B = FieldDesignChangeStatus.fromIdOrFail(b.statusId)
            return alphabeticalCompare(A, B, 'priority')
          },
          default: 'ascend',
          priority: 1,
        },
        filter: {
          with: 'items',
          key: 'statusId',
          items: FieldDesignChangeStatus.values
            .sort(alphabeticalSort('priority'))
            .map(({ id, name, description }) => ({
              text: name,
              tooltip: description,
              value: id,
            })),
        },
      },
      {
        title: 'Number',
        help: HelpMessage.FieldDesignChanges.FDCTracker.Number,
        render: 'formattedNumber',
        align: 'center',
        width: 135,
        sort: {
          with: 'key',
          key: 'formattedNumber',
          default: 'descend',
          priority: 2,
        },
        filter: { type: 'search', with: 'key', key: 'formattedNumber' },
      },
      externalLinks,
      {
        title: 'Disciplines',
        help: HelpMessage.FieldDesignChanges.FDCTracker.Disciplines,
        link: CIMSPage.FDCDisciplines,
        width: 150,
        render: ({ disciplines }) => (
          <CIMSTags
            tags={disciplines?.map(({ name, description }) => ({
              text: name,
              tooltip: description,
            }))}
          />
        ),
        filter: {
          loading: metadata?.loading,
          with: 'callback',
          items:
            metadata?.fdcDisciplines?.map(({ id, name, description }) => ({
              text: name,
              tooltip: description,
              value: id,
            })) ?? [],
          onFilter: (value, { disciplines }) =>
            disciplines.some(({ id }) => id === value),
        },
      },
      {
        title: 'Issue',
        help: HelpMessage.FieldDesignChanges.FDCTracker.Issue,
        render: 'issue',
        width: 300,
        filter: { type: 'search', with: 'key', key: 'issue' },
      },
      {
        title: 'Resolution',
        help: HelpMessage.FieldDesignChanges.FDCTracker.Resolution,
        render: (record) => <Resolution {...record} />,
        width: 300,
        filter: { type: 'search', with: 'key', key: 'resolution' },
      },
      {
        title: 'Level',
        help: HelpMessage.FieldDesignChanges.FDCTracker.ConcurrenceLevel,
        link: CIMSPage.FDCConcurrences,
        width: 125,
        render: ({ concurrenceLevel }) =>
          concurrenceLevel && (
            <CIMSTag tooltip={concurrenceLevel.description}>
              {concurrenceLevel.name}
            </CIMSTag>
          ),
        filter: {
          with: 'callback',
          loading: metadata?.loading,
          items:
            metadata?.fdcConcurrenceLevels?.map(
              ({ id, name, description }) => ({
                text: name,
                value: id,
                tooltip: description,
              })
            ) ?? [],
          onFilter: (value, { concurrenceLevel }) =>
            concurrenceLevel?.id === value,
        },
        sort: {
          with: 'callback',
          onCompare: (a, b) =>
            alphabeticalCompare(
              a?.concurrenceLevel,
              b?.concurrenceLevel,
              'name'
            ),
        },
      },
      {
        title: 'Level Concurrences',
        help: HelpMessage.FieldDesignChanges.FDCTracker.ConcurrenceGroups,
        link: CIMSPage.FDCConcurrences,
        width: {
          withoutRecords: 195,
          withRecords: (records) =>
            records.some(
              ({ statusId }) => statusId !== FieldDesignChangeStatus.Draft.id
            )
              ? 250
              : 195,
        },
        render: (record) => <ConcurrenceGroups {...record} />,
        filter: {
          type: 'checkboxes',
          loading: metadata?.loading,
          items: metadata?.fdcConcurrenceGroups?.map(
            ({ id, name, description }) => ({
              text: name,
              value: id,
              tooltip: description,
            })
          ),
          value: (concurrenceGroupId, { fdcToFDCConcurrenceGroups }) =>
            fdcToFDCConcurrenceGroups.find(
              ({ concurrenceGroup }) =>
                concurrenceGroup.id === concurrenceGroupId
            )?.concurred,
          skip: (_, { statusId }) =>
            // don't care if we're not at least pending levels
            statusId === FieldDesignChangeStatus.Draft.id,
          labels: {
            true: 'Concurred',
            // await concurrence because rejecting sends back to draft
            false: 'Awaiting Concurrence',
          },
        },
      },
      {
        title: 'Approvals',
        help: HelpMessage.FieldDesignChanges.FDCTracker.Approvals,
        link: CIMSPage.FDCApprovalGroups,
        width: { withRecords: 160, withoutRecords: 130 },
        render: (record) => <ApprovalGroups {...record} />,
        filter: {
          type: 'checkboxes',
          loading: metadata?.loading,
          items: metadata?.fdcApprovalGroups?.map(
            ({ id, name, description }) => ({
              text: name,
              tooltip: description,
              value: id,
            })
          ),
          value: (approvalGroupId, { fdcToFDCApprovalGroups }) =>
            fdcToFDCApprovalGroups.find(
              ({ approvalGroup }) => approvalGroup.id === approvalGroupId
            )?.approved,
          skip: (approvalGroupId, { statusId, concurrenceLevel }) => {
            /**
             * don't care if it's before pending approvals
             */
            const beforeApprovals = [
              FieldDesignChangeStatus.Draft.id,
              FieldDesignChangeStatus.PendingLevelConcurrences.id,
            ].some((id) => id === statusId)
            /**
             * don't care if the level doesn't have this group
             */
            const approvalGroup = metadata?.fdcConcurrenceLevels
              ?.find(({ id }) => id === concurrenceLevel?.id)
              ?.approvalGroups?.find(({ id }) => id === approvalGroupId)
            return !approvalGroup || beforeApprovals
          },
          labels: {
            true: 'Approved',
            // awaiting approval because you can't reject
            false: 'Awaiting Approval',
          },
        },
      },
    ],
    prominentCustomFields: {
      disableEditWhen: ({ statusId, deleted }) => ({
        alreadyDisabled:
          statusId === FieldDesignChangeStatus.Closed.id
            ? {
                why: 'invalid status',
                statusName: FieldDesignChangeStatus.Closed.name,
              }
            : false,
        recordInactive: deleted,
      }),
      onCheckBoolean: async ({ id, issue }, input) => {
        await edit.fn({
          variables: {
            input: { id, issue, customFields: { booleanInputs: [input] } },
          },
        })
        fieldDesignChanges?.refetch()
      },
      onCheckBooleans: async ({ id, issue }, input) => {
        await edit.fn({
          variables: {
            input: {
              id,
              issue,
              customFields: { listInputs: { checkboxes: [input] } },
            },
          },
        })
        fieldDesignChanges?.refetch()
      },
    },
  }
}
